import * as THREE from 'three';
import { TrainingTrack, TrainingTrackElement, TrainingTrackElementType, TrainingTrackProgress } from '../../../../shared/src/trainingTrack.types';
import { TrackElement } from './elements/TrackElement';
import { TrackElementFactory } from './elements/TrackElementFactory';
import { CheckpointElement } from './elements/CheckpointElement';
import { TargetElement } from './elements/TargetElement';
import { TrackEffectsManager } from './effects/TrackEffectsManager';
import { TRAINING_TRACK_CONFIG } from './config/TrainingTrackConfig';

const TRAINING_DEBUG = false;

function trainingDebugLog(...args: unknown[]): void {
    if (TRAINING_DEBUG) {
        console.log(...args);
    }
}

/**
 * Class responsible for managing training tracks
 */
export class TrainingTrackManager {
    private scene: THREE.Scene;
    private track: TrainingTrack | null = null;
    private trackElements: Map<string, TrackElement> = new Map();
    private progress: TrainingTrackProgress | null = null;
    private isActive: boolean = false;
    private startTime: number = 0;
    private playerPosition: THREE.Vector3 = new THREE.Vector3();
    private playerTank: any = null; // Will be set to the player's tank
    private effectsManager: TrackEffectsManager;
    private totalCheckpoints: number = 0;
    private readonly interactionDistanceSq = TRAINING_TRACK_CONFIG.INTERACTION_DISTANCE * TRAINING_TRACK_CONFIG.INTERACTION_DISTANCE;
    
    /**
     * Constructor
     * @param scene The Three.js scene
     */
    constructor(scene: THREE.Scene) {
        this.scene = scene;
        this.effectsManager = new TrackEffectsManager(scene);
    }

    /**
     * Loads a training track
     * @param track The training track to load
     * @param playerId The ID of the player
     */
    public loadTrack(track: TrainingTrack, playerId: string): void {
        // Clear any existing track
        this.clearTrack();

        this.track = track;
        this.totalCheckpoints = track.elements.reduce(
            (count, element) => count + (element.type === TrainingTrackElementType.CHECKPOINT ? 1 : 0),
            0
        );
        
        // Initialize progress tracking
        this.progress = {
            trackId: track.id,
            playerId: playerId,
            startTime: 0,
            currentTime: 0,
            checkpointsReached: [],
            targetsDestroyed: 0,
            score: 0,
            completed: false
        };

        // Create track elements
        track.elements.forEach(element => {
            this.createTrackElement(element);
        });

        console.log(`[TrainingTrack] Loaded track: ${track.name} with ${track.elements.length} elements`);
    }

    /**
     * Starts the training track
     */
    public startTrack(): void {
        if (!this.track || !this.progress) {
            console.error('[TrainingTrack] Cannot start track: No track loaded');
            return;
        }

        this.isActive = true;
        this.startTime = Date.now();
        this.progress.startTime = this.startTime;
        this.progress.currentTime = 0;
        this.progress.checkpointsReached = [];
        this.progress.targetsDestroyed = 0;
        this.progress.score = 0;
        this.progress.completed = false;

        // Activate the first checkpoint
        this.activateNextCheckpoint();

        console.log(`[TrainingTrack] Started track: ${this.track.name}`);

        // Dispatch event that track has started
        document.dispatchEvent(new CustomEvent('trainingTrack:started', { 
            detail: { trackId: this.track.id } 
        }));
    }

    /**
     * Stops the training track
     */
    public stopTrack(): void {
        if (!this.isActive) return;

        this.isActive = false;

        // Dispatch event that track has stopped
        if (this.track) {
            document.dispatchEvent(new CustomEvent('trainingTrack:stopped', { 
                detail: { 
                    trackId: this.track.id,
                    completed: this.progress?.completed || false,
                    progress: this.progress
                } 
            }));
        }

        console.log('[TrainingTrack] Stopped track');
    }

    /**
     * Clears the current track and removes all elements
     */
    public clearTrack(): void {
        // Remove all track elements from the scene
        this.trackElements.forEach(element => {
            element.remove(this.scene);
        });

        // Clear collections
        this.trackElements.clear();
        
        // Clear effects
        this.effectsManager.clear();

        // Reset state
        this.track = null;
        this.progress = null;
        this.isActive = false;
        this.startTime = 0;
        this.totalCheckpoints = 0;

        console.log('[TrainingTrack] Cleared track');
    }

    /**
     * Sets the player's tank reference
     * @param tank The player's tank
     */
    public setPlayerTank(tank: any): void {
        this.playerTank = tank;
    }

    /**
     * Updates the training track state
     * @param deltaTime Time elapsed since last update in seconds
     */
    public update(deltaTime: number): void {
        if (!this.isActive || !this.track || !this.progress || !this.playerTank) return;

        // Update player position
        this.playerPosition.copy(this.playerTank.mesh.position);

        // Update current time
        const currentTime = Date.now();
        this.progress.currentTime = currentTime - this.startTime;
        const sceneCamera = this.scene.userData.camera;

        // Update track elements
        this.trackElements.forEach(element => {
            // For target elements, pass the camera for health bar orientation.
            // Avoid double update calls for the same element per frame.
            if (element instanceof TargetElement && sceneCamera) {
                element.update(deltaTime, sceneCamera);
            } else {
                element.update(deltaTime);
            }
        });
        
        // Update effects
        this.effectsManager.update(deltaTime);

        // Check for interactions with track elements
        this.checkElementInteractions();
    }

    /**
     * Creates a track element from element data
     * @param element The element data
     */
    private createTrackElement(element: TrainingTrackElement): void {
        const trackElement = TrackElementFactory.createElement(element);
        
        if (trackElement) {
            // Create the visual representation
            trackElement.create(this.scene);
            
            // Store reference
            this.trackElements.set(element.id, trackElement);
        }
    }

    /**
     * Checks for interactions between the player and track elements
     */
    private checkElementInteractions(): void {
        if (!this.isActive || !this.track || !this.progress || !this.playerTank) return;

        this.trackElements.forEach((element, elementId) => {
            // Calculate distance to player
            const elementObj = element.getObject();
            if (!elementObj) return;

            const distanceSq = this.playerPosition.distanceToSquared(elementObj.position);

            // Check if player is within interaction distance
            if (distanceSq < this.interactionDistanceSq) {
                // Let the element handle the interaction
                const distance = Math.sqrt(distanceSq);
                const interactionHandled = element.handleInteraction(
                    this.playerPosition, 
                    this.playerTank, 
                    distance
                );
                
                if (interactionHandled) {
                    trainingDebugLog(`[TrainingTrack] Interaction handled for element ${elementId}`, {
                        type: element.getType(),
                        properties: element.getProperties()
                    });
                }
            }
        });
    }

    /**
     * Handles interaction with a checkpoint
     * @param checkpointId ID of the checkpoint
     * @param checkpoint The checkpoint element
     */
    private handleCheckpointInteraction(checkpointId: string, checkpoint: CheckpointElement): void {
        if (!this.progress) return;
        
        const checkpointNumber = checkpoint.getCheckpointNumber();
        
        // Only process active checkpoints that haven't been reached yet
        if (checkpoint.isActive() && !this.progress.checkpointsReached.includes(checkpointNumber)) {
            trainingDebugLog(`[TrainingTrack] Reached checkpoint ${checkpointNumber}`);
            
            // Add checkpoint to reached checkpoints
            this.progress.checkpointsReached.push(checkpointNumber);
            
            // Apply time bonus if any
            const timeBonus = checkpoint.getProperties().timeBonus || 0;
            if (timeBonus > 0) {
                this.startTime += timeBonus * 1000; // Convert seconds to milliseconds
                this.effectsManager.showScorePopup(checkpoint.getObject()!.position, `+${timeBonus}s`, 0x00ff00);
            }
            
            // Mark checkpoint as completed
            checkpoint.setActive(false);
            
            // Activate next checkpoint
            this.activateNextCheckpoint();
            
            // Check if all checkpoints have been reached
            this.checkTrackCompletion();
            
            // Dispatch event that checkpoint has been reached
            document.dispatchEvent(new CustomEvent('trainingTrack:checkpointReached', { 
                detail: { 
                    trackId: this.track?.id,
                    checkpointNumber: checkpointNumber,
                    progress: this.progress
                } 
            }));
        }
    }

    /**
     * Activates the next checkpoint in sequence
     */
    private activateNextCheckpoint(): void {
        if (!this.track || !this.progress) return;
        
        // Find the next checkpoint number
        let nextCheckpointNumber = 1; // Start with checkpoint 1
        
        if (this.progress.checkpointsReached.length > 0) {
            // Get the highest checkpoint number reached
            const maxReached = Math.max(...this.progress.checkpointsReached);
            nextCheckpointNumber = maxReached + 1;
        }
        
        // Find and activate the next checkpoint
        let found = false;
        
        this.trackElements.forEach((element) => {
            if (element instanceof CheckpointElement && 
                element.getCheckpointNumber() === nextCheckpointNumber) {
                
                // Activate the checkpoint
                element.setActive(true);
                found = true;
                
                trainingDebugLog(`[TrainingTrack] Activated checkpoint ${nextCheckpointNumber}`);
            }
        });
        
        if (!found && nextCheckpointNumber > 1) {
            // If no more checkpoints, mark track as completed
            this.completeTrack();
        }
    }

    /**
     * Checks if all checkpoints have been reached and completes the track if so
     */
    private checkTrackCompletion(): void {
        if (!this.track || !this.progress) return;

        // Check if all checkpoints have been reached
        if (this.totalCheckpoints > 0 && this.progress.checkpointsReached.length >= this.totalCheckpoints) {
            this.completeTrack();
        }
    }

    /**
     * Marks the track as completed
     */
    private completeTrack(): void {
        if (!this.track || !this.progress || this.progress.completed) return;
        
        this.progress.completed = true;
        
        // Calculate final time and score
        const finalTime = this.progress.currentTime / 1000; // Convert to seconds
        
        // Update best time and score if better than previous
        if (!this.track.bestTime || finalTime < this.track.bestTime) {
            this.track.bestTime = finalTime;
        }
        
        if (!this.track.bestScore || this.progress.score > this.track.bestScore) {
            this.track.bestScore = this.progress.score;
        }
        
        console.log(`[TrainingTrack] Track completed! Time: ${finalTime.toFixed(2)}s, Score: ${this.progress.score}`);
        
        // Dispatch event that track has been completed
        document.dispatchEvent(new CustomEvent('trainingTrack:completed', { 
            detail: { 
                trackId: this.track.id,
                time: finalTime,
                score: this.progress.score,
                targetsDestroyed: this.progress.targetsDestroyed,
                bestTime: this.track.bestTime,
                bestScore: this.track.bestScore
            } 
        }));
        
        // Stop the track after a short delay
        setTimeout(() => {
            this.stopTrack();
        }, 3000);
    }

    /**
     * Handles damage to a target
     * @param targetId ID of the target
     * @param damage Amount of damage to apply
     * @returns True if the target was destroyed, false otherwise
     */
    public damageTarget(targetId: string, damage: number): boolean {
        if (!this.isActive || !this.progress) return false;
        
        const targetElement = this.trackElements.get(targetId);
        if (!targetElement || !(targetElement instanceof TargetElement)) {
            return false;
        }
        
        // Apply damage to target
        const destroyed = targetElement.applyDamage(damage);
        
        if (destroyed) {
            // Target destroyed
            console.log(`[TrainingTrack] Target destroyed: ${targetId}`);
            
            // Add points to score
            const points = targetElement.getProperties().points || 100;
            this.progress.score += points;
            this.progress.targetsDestroyed++;
            
            // Show score popup
            const targetObj = targetElement.getObject();
            if (targetObj) {
                this.effectsManager.showScorePopup(targetObj.position, `+${points}`, 0xffff00);
            }
            
            // Handle cascading targets
            if (targetElement.isCascading()) {
                this.triggerCascadingExplosion(targetObj!.position);
            }
            
            // Remove target from scene
            targetElement.remove(this.scene);
            this.trackElements.delete(targetId);
        }
        
        return destroyed;
    }
    
    /**
     * Triggers a cascading explosion effect at the given position
     * @param position The position to trigger the explosion at
     */
    private triggerCascadingExplosion(position: THREE.Vector3): void {
        if (!this.isActive || !this.progress) return;
        const cascadeRadiusSq = TRAINING_TRACK_CONFIG.CASCADE_RADIUS * TRAINING_TRACK_CONFIG.CASCADE_RADIUS;
        
        // Create explosion effect
        this.effectsManager.createExplosion(position);
        
        // Find nearby targets within cascade radius
        this.trackElements.forEach((element, elementId) => {
            if (element instanceof TargetElement && element.isCascadable()) {
                const targetObj = element.getObject();
                if (!targetObj) return;

                const distanceSq = position.distanceToSquared(targetObj.position);

                // If target is within cascade radius, damage it
                if (distanceSq < cascadeRadiusSq) {
                    // Apply cascade damage
                    this.damageTarget(elementId, TRAINING_TRACK_CONFIG.CASCADE_DAMAGE);
                }
            }
        });
    }
    
    /**
     * Gets the current track progress
     * @returns The current track progress or null if no track is active
     */
    public getProgress(): TrainingTrackProgress | null {
        return this.progress;
    }
    
    /**
     * Updates the player's position and tank reference
     * @param position The player's position
     * @param tank The player's tank
     */
    public updatePlayerPosition(position: THREE.Vector3, tank: any): void {
        this.playerPosition.copy(position);
        this.playerTank = tank;
    }

    /**
     * Gets the track
     * @returns The current track or null if no track is loaded
     */
    public getTrack(): TrainingTrack | null {
        return this.track;
    }
    
    /**
     * Checks if a track is currently active
     * @returns True if a track is active, false otherwise
     */
    public isTrackActive(): boolean {
        return this.isActive;
    }
}
